import { useEffect, useRef } from 'react';

/**
 * This effect activates a document listener that runs a handler when clicking outside the passed container
 *
 * @param {object} options
 * @param {object} options.container - a ref to a dom element that defines the container outside of which the
 *                                     handler will fire when a user clicks
 * @param {func}   options.handler - the function to run when a click outside the container is registered
 * @param {bool}   options.listenerConditional - a conditional that limits when the document listener is active
 * @param {array}  dependencies - an array of values (such as state/props/callbacks) that this hook needs to be
 *                                aware of when they change. This follows the regular hooks dependency array pattern.
 */
export default function useClickOutside({ container, handler, listenerConditional = true }, dependencies) {
  const isTouch = useRef(false);
  const handleClickOutside = (e) => {
    // the following touchend code is copied over from withClickOutside to match functionality
    if (e.type === 'touchend') {
      isTouch.current = true;
    }
    if (e.type === 'mousedown' && isTouch.current) {
      return;
    }

    if (container.current && !container.current.contains(e.target)) {
      handler(e);
    }
  };

  useEffect(() => {
    if (listenerConditional) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, dependencies);
}
